/**
 * Electric sync configuration helpers.
 *
 * Supports two upstreams behind the same /v1/shape API:
 *  - Electric Cloud (sunset): GET https://api.electric-sql.cloud/v1/shape?table=...&source_id=...&secret=...
 *  - Self-hosted Electric (infra/electric/): GET https://<host>/v1/shape?table=...&secret=...
 *    Self-hosted ignores source_id; ELECTRIC_SECRET gates the API instead.
 *  - Durable streams: PUT/GET https://api.electric-sql.cloud/v1/stream/<service-id>/<stream-name>
 *
 * Service secrets must never ship to the browser — inject them in a server proxy.
 */

export const ELECTRIC_CLOUD_API_ORIGIN = "https://api.electric-sql.cloud";

export const ELECTRIC_CLOUD_SHAPE_PATH = "/v1/shape";
export const ELECTRIC_CLOUD_STREAM_PATH_PREFIX = "/v1/stream";

export interface ElectricCloudServicesConfig {
  postgresSourceId: string;
  streamsServiceId: string;
  streamsBaseUrl?: string;
}

/** Well-known durable stream names for the FlowState agent platform. */
export const FLOWSTATE_DURABLE_STREAMS = {
  myceliumGlobal: "mycelium/global",
  cortexSessions: "cortex/sessions",
  agentDaemonHeartbeat: "agent/daemon-heartbeat",
} as const;

export type FlowstateDurableStreamName =
  (typeof FLOWSTATE_DURABLE_STREAMS)[keyof typeof FLOWSTATE_DURABLE_STREAMS];

export interface BuildShapeUrlOptions {
  where?: string;
  columns?: string[];
}

function isLegacyTokenSubdomain(baseUrl: string): boolean {
  return (
    baseUrl.includes(".electric-sql.cloud") &&
    !baseUrl.includes("api.electric-sql.cloud")
  );
}

function resolveShapeEndpoint(baseUrl: string): string {
  const trimmed = baseUrl.replace(/\/$/, "");
  if (trimmed.endsWith(ELECTRIC_CLOUD_SHAPE_PATH)) return trimmed;
  if (trimmed.startsWith("http")) return `${trimmed}${ELECTRIC_CLOUD_SHAPE_PATH}`;
  return trimmed.startsWith("/")
    ? trimmed
    : `/${trimmed}`.replace(/\/+/g, "/");
}

/**
 * Build a client-safe shape URL (no secrets).
 * Defaults to the app proxy path `/api/electric/shape`.
 */
export function buildElectricShapeUrl(
  shapeBaseUrl: string,
  table: string,
  options?: BuildShapeUrlOptions
): string {
  const where = options?.where;
  const columns = options?.columns;

  if (isLegacyTokenSubdomain(shapeBaseUrl)) {
    const legacyBase = `${shapeBaseUrl.replace(/\/$/, "")}${ELECTRIC_CLOUD_SHAPE_PATH}/${table}`;
    if (!where && !columns?.length) return legacyBase;
    const url = new URL(legacyBase);
    if (where) url.searchParams.set("where", where);
    if (columns?.length) url.searchParams.set("columns", columns.join(","));
    return url.toString();
  }

  const endpoint = resolveShapeEndpoint(shapeBaseUrl);
  const isAbsolute = endpoint.startsWith("http");
  const url = new URL(
    endpoint,
    isAbsolute ? undefined : "http://flowstate.local"
  );
  url.searchParams.set("table", table);
  if (where) url.searchParams.set("where", where);
  if (columns?.length) url.searchParams.set("columns", columns.join(","));

  if (isAbsolute) return url.toString();
  return `${url.pathname}${url.search}`;
}

export function buildElectricStreamUrl(
  streamsBaseUrl: string,
  streamName: FlowstateDurableStreamName | string
): string {
  const base = streamsBaseUrl.replace(/\/$/, "");
  const encoded = streamName
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return `${base}/${encoded}`;
}

export function getDefaultStreamsBaseUrl(streamsServiceId: string): string {
  return `${ELECTRIC_CLOUD_API_ORIGIN}${ELECTRIC_CLOUD_STREAM_PATH_PREFIX}/${streamsServiceId}`;
}

export interface ElectricShapeProxyConfig {
  /**
   * Electric Cloud source id (svc-...). Omitted for self-hosted Electric,
   * which has no source registry — only `secret` is sent upstream.
   */
  sourceId?: string;
  /**
   * Upstream secret — Electric Cloud source secret, or self-hosted
   * `ELECTRIC_SECRET`. Injected as the `secret` query param.
   */
  sourceSecret: string;
  /**
   * Upstream origin (default: Electric Cloud API). Point at a self-hosted
   * Electric instance (e.g. `https://<service>.up.railway.app`) — the
   * `/v1/shape` path is identical.
   */
  origin?: string;
  /**
   * Supabase project URL. Required — the proxy verifies the caller's JWT and
   * resolves org memberships before authorizing the shape. The caller's own
   * JWT is used for lookup queries so RLS remains the access oracle.
   */
  supabaseUrl: string;
  /** Publishable/anon key paired with the caller's JWT for REST lookups. */
  supabaseAnonKey: string;
  /** TTL for the token → auth-context cache (default 5 min). */
  authCacheTtlMs?: number;
  /** Injectable fetch for testing. */
  fetcher?: typeof fetch;
}

// ── Shape authorization ─────────────────────────────────────────────────────

/**
 * Client-declared scope for a shape request. The proxy — not the client —
 * builds the SQL `where` clause from these, after verifying the caller's JWT.
 * Scope ids are always validated as UUIDs (or safe slugs for sessions) so
 * there is no WHERE-injection surface.
 */
export interface ShapeScope {
  user?: string;
  org?: string;
  task?: string;
  client?: string;
  session?: string;
}

interface ShapeAuthContext {
  uid: string;
  orgIds: string[];
  /**
   * RLS-scoped existence check — asks "can this caller see row `id` in
   * `table`?" by querying REST with the caller's own JWT.
   */
  assertAccess: (table: string, id: string) => Promise<boolean>;
}

type ShapeAuthResult =
  | { ok: true; where: string }
  | { ok: false; status: number; error: string };

const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
/** Session ids are TEXT (uuid or slug) — constrain to a safe character set. */
const SAFE_ID_RE = /^[A-Za-z0-9._:-]{1,128}$/;

function sqlLit(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

function deny(error: string, status = 403): ShapeAuthResult {
  return { ok: false, status, error };
}

function ok(where: string): ShapeAuthResult {
  return { ok: true, where };
}

function selfOnly(scope: ShapeScope, ctx: ShapeAuthContext): ShapeAuthResult | null {
  if (scope.user !== ctx.uid) {
    return deny("scope_user must equal the authenticated user");
  }
  return null;
}

/**
 * Self-scope required + optional org narrowing. For tables whose RLS is
 * self-only (`user_id = auth.uid()` / `is_owner`): a bare `scope_org` can
 * never widen beyond the caller's own rows, so user scope is mandatory.
 */
function selfWithOptionalOrg(
  scope: ShapeScope,
  ctx: ShapeAuthContext,
  userCol = "user_id",
  orgCol = "organization_id"
): ShapeAuthResult {
  const e = selfOnly(scope, ctx);
  if (e) return e;
  const parts = [`${userCol} = ${sqlLit(ctx.uid)}`];
  if (scope.org) {
    const r = orgOnly({ org: scope.org }, ctx, orgCol);
    if (!r.ok) return r;
    parts.push(r.where);
  }
  return ok(parts.join(" AND "));
}

/**
 * Org-member scope — the WHERE covers every row in the org. `scope_user`
 * (if also sent) is ignored: org rules don't filter by it, and a foreign
 * scope_user can never expand access beyond the caller's own orgs.
 */
function orgOnly(
  scope: ShapeScope,
  ctx: ShapeAuthContext,
  column = "organization_id"
): ShapeAuthResult {
  if (!scope.org) return deny(`Missing scope_org`);
  if (!UUID_RE.test(scope.org)) return deny("scope_org must be a uuid", 400);
  if (!ctx.orgIds.includes(scope.org)) return deny("Not a member of scope_org");
  return ok(`${column} = ${sqlLit(scope.org)}`);
}

async function entityScope(
  entityTable: string,
  id: string | undefined,
  column: string,
  ctx: ShapeAuthContext
): Promise<ShapeAuthResult> {
  if (!id) return deny(`Missing ${column} scope`);
  if (!UUID_RE.test(id)) return deny(`${column} scope must be a uuid`, 400);
  if (!(await ctx.assertAccess(entityTable, id))) {
    return deny(`No access to ${entityTable} ${id}`);
  }
  return ok(`${column} = ${sqlLit(id)}`);
}

/**
 * Per-table authorization rules for shape requests.
 *
 * Every synced table maps scope params → a server-built WHERE. Rules:
 *  - `scope_user` may only ever equal the authenticated uid (self-scope).
 *  - `scope_org` must be one of the caller's `organization_members` rows.
 *  - Entity scopes (`scope_task`/`scope_client`/`scope_session`) are checked
 *    by an RLS-scoped existence query — visible ⇒ accessible.
 *  - Tables not listed here are denied outright.
 */
const SHAPE_TABLE_RULES: Record<
  string,
  (scope: ShapeScope, ctx: ShapeAuthContext) => ShapeAuthResult | Promise<ShapeAuthResult>
> = {
  notifications: (s, c) => selfOnly(s, c) ?? ok(`user_id = ${sqlLit(c.uid)}`),
  profiles: (s, c) => selfOnly(s, c) ?? ok(`id = ${sqlLit(c.uid)}`),
  nutrition_entries: (s, c) => selfOnly(s, c) ?? ok(`user_id = ${sqlLit(c.uid)}`),
  exercise_entries: (s, c) => selfOnly(s, c) ?? ok(`user_id = ${sqlLit(c.uid)}`),
  health_metric_entries: (s, c) =>
    selfOnly(s, c) ?? ok(`user_id = ${sqlLit(c.uid)}`),
  health_goals: (s, c) => selfOnly(s, c) ?? ok(`user_id = ${sqlLit(c.uid)}`),

  todos: (s, c) => {
    if (s.org) return orgOnly({ org: s.org }, c);
    const e = selfOnly(s, c);
    if (e) return e;
    return ok(
      `(created_by = ${sqlLit(c.uid)} OR assigned_to = ${sqlLit(c.uid)})`
    );
  },
  clients: (s, c) => {
    if (s.org) return orgOnly({ org: s.org }, c);
    const e = selfOnly(s, c);
    if (e) return e;
    return ok(
      `(created_by = ${sqlLit(c.uid)} OR assigned_to = ${sqlLit(c.uid)})`
    );
  },
  opportunities: (s, c) => orgOnly(s, c),
  cortex_events_log: (s, c) => orgOnly(s, c, "org_id"),

  // RLS is self-only on these three — an org scope may narrow but never
  // replace the user predicate.
  time_blocks: (s, c) => selfWithOptionalOrg(s, c),
  accounts: (s, c) => selfWithOptionalOrg(s, c),
  categories_accounting: (s, c) => selfWithOptionalOrg(s, c),

  client_activities: async (s, c) => {
    if (s.client) {
      return entityScope("clients", s.client, "client_id", c);
    }
    const e = selfOnly(s, c);
    if (e) return e;
    return ok(`created_by = ${sqlLit(c.uid)}`);
  },
  task_comments: async (s, c) =>
    entityScope("todos", s.task, "task_id", c),
  task_dependencies: async (s, c) => {
    const r = await entityScope(
      "todos",
      s.task,
      "predecessor_task_id",
      c
    );
    if (!r.ok) return r;
    return ok(
      `(predecessor_task_id = ${sqlLit(s.task!)} OR successor_task_id = ${sqlLit(s.task!)})`
    );
  },
  cortex_runs: async (s, c) => {
    if (!s.session) return deny("Missing scope_session");
    if (!SAFE_ID_RE.test(s.session)) {
      return deny("scope_session contains illegal characters", 400);
    }
    // session_id is polymorphic across writers — accept when the caller can
    // see the session in either session table (both are self-RLS scoped).
    const visible =
      (await c.assertAccess("cortex_sessions", s.session)) ||
      (await c.assertAccess("chat_sessions", s.session));
    if (!visible) return deny(`No access to session ${s.session}`);
    return ok(`session_id = ${sqlLit(s.session)}`);
  },
};

/** Token → {uid, orgIds} cache so live long-polls don't re-verify each cycle. */
const authContextCache = new Map<
  string,
  { uid: string; orgIds: string[]; expiresAt: number }
>();
const AUTH_CACHE_TTL_MS = 5 * 60 * 1000;

interface VerifiedCaller {
  uid: string;
  orgIds: string[];
}

async function verifyCaller(
  token: string,
  config: ElectricShapeProxyConfig,
  fetcher: typeof fetch
): Promise<VerifiedCaller | null> {
  const cached = authContextCache.get(token);
  if (cached && cached.expiresAt > Date.now()) {
    return { uid: cached.uid, orgIds: cached.orgIds };
  }

  const base = config.supabaseUrl.replace(/\/$/, "");
  const headers = {
    apikey: config.supabaseAnonKey,
    authorization: `Bearer ${token}`,
  };

  const userRes = await fetcher(`${base}/auth/v1/user`, { headers });
  if (!userRes.ok) return null;
  const user = (await userRes.json()) as { id?: string };
  if (!user.id) return null;

  // RLS (users_view_org_members) returns this caller's own membership rows.
  const orgRes = await fetcher(
    `${base}/rest/v1/organization_members?select=organization_id&user_id=eq.${user.id}`,
    { headers }
  );
  const orgIds = orgRes.ok
    ? ((await orgRes.json()) as Array<{ organization_id?: string }>)
        .map((r) => r.organization_id)
        .filter((x): x is string => Boolean(x))
    : [];

  const entry = {
    uid: user.id,
    orgIds,
    expiresAt: Date.now() + (config.authCacheTtlMs ?? AUTH_CACHE_TTL_MS),
  };
  if (authContextCache.size > 500) authContextCache.clear();
  authContextCache.set(token, entry);
  return entry;
}

async function assertEntityAccess(
  entityTable: string,
  id: string,
  token: string,
  config: ElectricShapeProxyConfig,
  fetcher: typeof fetch
): Promise<boolean> {
  const base = config.supabaseUrl.replace(/\/$/, "");
  const res = await fetcher(
    `${base}/rest/v1/${entityTable}?select=id&id=eq.${encodeURIComponent(id)}&limit=1`,
    {
      headers: {
        apikey: config.supabaseAnonKey,
        authorization: `Bearer ${token}`,
      },
    }
  );
  if (!res.ok) return false;
  const rows = (await res.json()) as unknown[];
  return Array.isArray(rows) && rows.length > 0;
}

/** Electric protocol params allowed to pass through to Cloud. */
const FORWARDED_PARAMS = new Set([
  "table",
  "columns",
  "offset",
  "handle",
  "live",
  "live_sse",
  "experimental_live_sse",
  "cursor",
  "expired_handle",
  "log",
  "cache-buster",
  "api_version",
  "replica",
]);

function jsonError(status: number, error: string): Response {
  return new Response(JSON.stringify({ error }), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

/**
 * Proxy an incoming shape request to Electric Cloud, injecting source credentials.
 *
 * Security model:
 *  1. Caller must present a valid Supabase JWT (Authorization: Bearer).
 *  2. Raw `where` params are rejected — the proxy builds the WHERE from
 *     `scope_*` params after verifying the caller's identity, org
 *     memberships, and entity access via RLS-scoped lookups.
 *  3. Tables not in SHAPE_TABLE_RULES are denied outright.
 *
 * Strips content-encoding headers per Electric Cloud proxy guidance.
 */
export async function proxyElectricShapeRequest(
  request: Request,
  config: ElectricShapeProxyConfig
): Promise<Response> {
  const fetcher = config.fetcher ?? fetch;
  const incoming = new URL(request.url);

  const table = incoming.searchParams.get("table");
  if (!table) return jsonError(400, "Missing required query param: table");

  const rule = SHAPE_TABLE_RULES[table];
  if (!rule) return jsonError(403, `Table '${table}' is not exposed via shapes`);

  // Raw WHERE is never accepted — scope_* params are the only filter surface.
  if (incoming.searchParams.has("where")) {
    return jsonError(403, "Raw 'where' param is not accepted; use scope_* params");
  }

  const auth = request.headers.get("authorization");
  const token = auth?.startsWith("Bearer ") ? auth.slice(7).trim() : "";
  if (!token) return jsonError(401, "Missing bearer token");

  const caller = await verifyCaller(token, config, fetcher);
  if (!caller) return jsonError(401, "Invalid or expired token");

  const scope: ShapeScope = {
    user: incoming.searchParams.get("scope_user") ?? undefined,
    org: incoming.searchParams.get("scope_org") ?? undefined,
    task: incoming.searchParams.get("scope_task") ?? undefined,
    client: incoming.searchParams.get("scope_client") ?? undefined,
    session: incoming.searchParams.get("scope_session") ?? undefined,
  };
  for (const value of [scope.user, scope.org, scope.task, scope.client]) {
    if (value !== undefined && !UUID_RE.test(value)) {
      return jsonError(400, "scope ids must be uuids");
    }
  }

  const authz = await rule(scope, {
    uid: caller.uid,
    orgIds: caller.orgIds,
    assertAccess: (entityTable, id) =>
      assertEntityAccess(entityTable, id, token, config, fetcher),
  });
  if (!authz.ok) return jsonError(authz.status, authz.error);

  const origin = config.origin ?? ELECTRIC_CLOUD_API_ORIGIN;
  const target = new URL(`${origin}${ELECTRIC_CLOUD_SHAPE_PATH}`);
  incoming.searchParams.forEach((value, key) => {
    if (FORWARDED_PARAMS.has(key) || key.startsWith("subset__")) {
      target.searchParams.set(key, value);
    }
  });
  target.searchParams.set("table", table);
  target.searchParams.set("where", authz.where);
  if (config.sourceId) target.searchParams.set("source_id", config.sourceId);
  target.searchParams.set("secret", config.sourceSecret);

  const headers = new Headers();
  const accept = request.headers.get("accept");
  if (accept) headers.set("accept", accept);

  let response: Response;
  try {
    response = await fetcher(target.toString(), {
      method: request.method,
      headers,
      signal: request.signal,
    });
  } catch {
    return jsonError(502, "Upstream Electric service unreachable");
  }

  const outHeaders = new Headers(response.headers);
  outHeaders.delete("content-encoding");
  outHeaders.delete("content-length");
  outHeaders.set(
    "Access-Control-Expose-Headers",
    "electric-offset, electric-handle, electric-schema, electric-cursor"
  );

  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers: outHeaders,
  });
}

export interface ElectricStreamProxyConfig {
  streamsBaseUrl: string;
  streamsSecret: string;
  streamPath: string;
}

/** Proxy a durable stream read/write to Electric Cloud with service auth. */
export async function proxyElectricStreamRequest(
  request: Request,
  config: ElectricStreamProxyConfig
): Promise<Response> {
  const base = config.streamsBaseUrl.replace(/\/$/, "");
  const path = config.streamPath.replace(/^\/+/, "");
  const target = `${base}/${path}${new URL(request.url).search}`;

  const headers = new Headers();
  const contentType = request.headers.get("content-type");
  if (contentType) headers.set("content-type", contentType);
  const accept = request.headers.get("accept");
  if (accept) headers.set("accept", accept);
  headers.set("authorization", `Bearer ${config.streamsSecret}`);

  let response: Response;
  try {
    response = await fetch(target, {
      method: request.method,
      headers,
      body: ["GET", "HEAD", "OPTIONS"].includes(request.method)
        ? undefined
        : request.body,
      // @ts-expect-error duplex required for streaming bodies in Node 18+
      duplex: "half",
      signal: request.signal,
    });
  } catch {
    return jsonError(502, "Upstream streams service unreachable");
  }

  const outHeaders = new Headers(response.headers);
  outHeaders.delete("content-encoding");
  outHeaders.delete("content-length");

  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers: outHeaders,
  });
}
